/**
 * Milking module — recommended order for farms new to bulk milk + herd recording.
 * Used by MilkingWorkflowGuide and sidebar group hints.
 */

export const MILKING_WORKFLOW_INTRO =
  'Follow these steps in order the first time. After setup, your daily loop is: Real-time milking → record what left the tank → check Daily Milk Balance.';

export const MILKING_WORKFLOW_PHASES = [
  {
    step: 1,
    title: 'One-time setup (tanks & routing)',
    when: 'Before the first milking session',
    summary: 'Tell the system where bulk milk is stored and how each shift fills the tanks.',
    links: [
      { path: '/milking/tanks', label: 'Chillers & Tanks', detail: 'Register bulk tank, overflow chiller, capacities' },
      { path: '/milking/routing', label: 'Session → Tank Routing', detail: 'Morning/evening milk → which tank (no pick per cow)' },
      { path: '/milking/buyers', label: 'Buyers & Pricing', detail: 'Optional — if you sell to a dairy or customers' },
    ],
  },
  {
    step: 2,
    title: 'Record milking (animals → tanks)',
    when: 'Every milking shift',
    summary: 'Log yield per animal; litres route to tanks automatically from step 1.',
    links: [
      { path: '/animals/real-time-milking', label: 'Real-time milking', detail: 'Timers, quantities, health flags — Save when done' },
      { path: '/milking', label: 'Milking overview → Herd production', detail: 'Charts, missed animals, underperformers' },
    ],
  },
  {
    step: 3,
    title: 'Record milk leaving the tank',
    when: 'Whenever milk is sold, fed, used, processed, or dumped',
    summary: 'A disposition removes litres from a tank so balances stay correct.',
    links: [
      { path: '/milking/dispositions', label: 'Record Disposition', detail: 'Sales, farm use, or any bulk movement out' },
      { path: '/milking/calves', label: 'Young stock & colostrum', detail: 'Quick daily allocation for calves, lambs, kids, foals' },
      { path: '/milking/workers', label: 'Worker & Farm Use', detail: 'Staff / household milk' },
      { path: '/milking/processing', label: 'On-Farm Processing', detail: 'Paneer, ghee, dahi from tank milk' },
      { path: '/milking/wastage', label: 'Wastage Records', detail: 'Spillage, quality reject, mastitis, etc.' },
    ],
  },
  {
    step: 4,
    title: 'Close the day (reconcile)',
    when: 'End of day or before next shift',
    summary: 'Confirm production minus dispositions matches what is still in the tanks.',
    links: [
      { path: '/milking/balance', label: 'Daily Milk Balance', detail: 'Opening + production − dispositions = closing' },
      { path: '/milking', query: 'tab=balance', label: 'Milking overview → Milk balance & tanks', detail: 'Tank levels, alerts, today’s KPIs' },
      { path: '/milking/collections', label: 'Collection Records', detail: 'Review sale pickups linked to buyers' },
    ],
  },
  {
    step: 5,
    title: 'Ongoing care (optional)',
    when: 'As needed',
    summary: 'Cooling compliance and quality paperwork.',
    links: [
      { path: '/milking/tank-logs', label: 'Temp & CIP logs', detail: 'Chiller temperature and cleaning records' },
      { path: '/milking/quality', label: 'Quality & Testing', detail: 'Lab / field test results' },
    ],
  },
];

/** Sidebar menu group hints (key matches Layout group keys) */
export const MILKING_MENU_GROUP_HINTS = {
  'milking-group-step-1': { stepLabel: '1', title: 'Setup', hint: 'Tanks, routing, buyers' },
  'milking-group-step-2': { stepLabel: '2', title: 'Record milking', hint: 'Every shift' },
  'milking-group-step-3': { stepLabel: '3', title: 'Milk out of tank', hint: 'Sales, feed, use, waste' },
  'milking-group-step-4': { stepLabel: '4', title: 'Close the day', hint: 'Balance & collections' },
  'milking-group-step-5': { stepLabel: '5', title: 'Ongoing care', hint: 'Cooling & quality logs' },
};

/** Menu keys that are not plain `/${key}` paths */
export const MILKING_MENU_ROUTE_OVERRIDES = {
  'milking-tab-balance': '/milking?tab=balance',
};

export const MILKING_GUIDE_HASH = 'milking-workflow-guide';

export function milkingWorkflowPath(link) {
  if (link.query) {
    return `/milking?${link.query}`;
  }
  return link.path;
}
